import React, { useEffect, useState } from 'react';
import {
  Check,
  Copy,
  Key,
  Loader2,
  Plus,
  RotateCw,
  Shield,
  Trash2,
  X,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import type { ApiKeyDto } from '@antigravity/types';

export const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('campaigns:read,campaigns:write');
  const [expiresInDays, setExpiresInDays] = useState('90');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getApiKeys();
      setKeys(data);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to fetch API keys' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      setSubmitting(true);
      const scopeArray = scopes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const res = await apiClient.createApiKey({
        name: name.trim(),
        scopes: scopeArray.length > 0 ? scopeArray : ['*'],
        expiresInDays: parseInt(expiresInDays, 10) || 90,
      });

      setCreatedSecret(res.rawSecretKey);
      setName('');
      await loadKeys();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to create API key' });
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }
    try {
      await apiClient.revokeApiKey(id);
      setMessage({ type: 'success', text: 'API key revoked successfully' });
      await loadKeys();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to revoke API key' });
    }
  };

  const handleRotate = async (id: string) => {
    if (!window.confirm('Rotating this key will revoke the existing key and generate a new secret. Proceed?')) {
      return;
    }
    try {
      const res = await apiClient.rotateApiKey(id);
      setCreatedSecret(res.rawSecretKey);
      setModalOpen(true);
      await loadKeys();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to rotate API key' });
    }
  };

  const copyToClipboard = () => {
    if (!createdSecret) return;
    navigator.clipboard.writeText(createdSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Key className="w-6 h-6 text-brand-400" />
            <span>API Keys & Machine Authentication</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Generate and manage scoped API keys for automated integrations, CLI tools, and background sync jobs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setCreatedSecret(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-semibold transition-all shadow-lg shadow-brand-600/25"
          >
            <Plus className="w-4 h-4" />
            <span>Create New API Key</span>
          </button>
        </div>
      </div>

      {/* Alert banner */}
      {message && (
        <div
          className={`p-4 rounded-xl text-sm border flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-300'
              : 'bg-red-950/40 border-red-500/30 text-red-300'
          }`}
        >
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className="text-xs font-mono opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Keys Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin text-brand-400 mb-3" />
          <p className="text-sm">Loading API keys...</p>
        </div>
      ) : keys.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-900/40 border border-slate-800 text-center space-y-3">
          <Key className="w-10 h-10 text-slate-600 mx-auto" />
          <h3 className="text-base font-bold text-white">No API Keys Generated</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            Create an API key to access the Antigravity programmatic API securely from your applications or scripts.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-semibold transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Create Your First Key</span>
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-900/40">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
              <tr>
                <th className="px-4 py-3">Key Name</th>
                <th className="px-4 py-3">Prefix / ID</th>
                <th className="px-4 py-3">Scopes</th>
                <th className="px-4 py-3">Last Used</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {keys.map((k) => (
                <tr key={k.id} className="hover:bg-slate-800/30">
                  <td className="px-4 py-3 font-semibold text-white">{k.name}</td>
                  <td className="px-4 py-3 font-mono text-slate-400">
                    {k.keyPrefix}••••••••
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <span
                          key={s}
                          className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] font-mono text-slate-300"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-[11px]">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-[11px]">
                    {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'No expiry'}
                  </td>
                  <td className="px-4 py-3">
                    {k.isRevoked ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/20">
                        Revoked
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!k.isRevoked && (
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleRotate(k.id)}
                          title="Rotate key"
                          className="p-1.5 text-slate-400 hover:text-brand-400 hover:bg-brand-500/10 rounded-lg transition-colors"
                        >
                          <RotateCw className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleRevoke(k.id)}
                          title="Revoke key"
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Reveal Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-800 p-6 shadow-2xl relative">
            <button
              onClick={() => {
                setModalOpen(false);
                setCreatedSecret(null);
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {createdSecret ? (
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                  <Shield className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-white">Save Your API Key Now</h3>
                <p className="text-xs text-amber-300 bg-amber-950/30 border border-amber-500/20 p-3 rounded-xl leading-relaxed">
                  ⚠️ <strong>Security Notice:</strong> This secret key is shown only once and will never be displayed again. Store it securely in your secret manager or environment configuration.
                </p>

                <div className="space-y-1">
                  <label className="text-[11px] font-semibold text-slate-400 uppercase">
                    Secret API Key
                  </label>
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-950 border border-slate-800 font-mono text-xs text-emerald-400 select-all break-all">
                    <span>{createdSecret}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold text-xs transition-colors shadow-lg shadow-brand-600/25"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span>{copied ? 'Copied to Clipboard!' : 'Copy Key'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setModalOpen(false);
                      setCreatedSecret(null);
                    }}
                    className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs transition-colors"
                  >
                    I Have Saved It
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreate} className="space-y-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-brand-400" />
                  <span>Create API Key</span>
                </h3>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Key Description / Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Production CI/CD, Zapier Sync"
                    required
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Scopes (Comma-separated)
                  </label>
                  <input
                    type="text"
                    value={scopes}
                    onChange={(e) => setScopes(e.target.value)}
                    placeholder="e.g. campaigns:read,campaigns:write"
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 font-mono text-[11px]"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Use * for full permissions.</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1">
                    Expiration Period
                  </label>
                  <select
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  >
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>

                <div className="pt-2 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold text-xs transition-colors flex items-center gap-2 shadow-lg shadow-brand-600/25"
                  >
                    {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>Generate Secret Key</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
